import { useEffect, useMemo, useState, type ComponentType, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { Bar, CartesianGrid, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import {
  Activity,
  AlertTriangle,
  Info,
  ListOrdered,
  MailOpen,
  MousePointerClick,
  RefreshCcw,
  Reply,
  Rocket,
  Send,
} from "lucide-react";
import {
  errorMessage,
  getCampaignPerformance,
  type CampaignPerformance,
  type LeadFunnel,
  type MessageTotals,
  type PerformanceRates,
  type StepPerformance,
} from "@/api/email";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Hint } from "@/components/email/EmailBadges";
import {
  CONTACT_STATUS,
  CONTACT_STATUS_ORDER,
  METRIC_HINT,
  activitySeries,
  isoDayIn,
  periodRange,
  plural,
  rateLabel,
  type ActivityKey,
  type PeriodPreset,
} from "@/lib/email";
import { formatNumber } from "@/lib/formatters";
import { cn } from "@/lib/utils";
import { useWorkspace } from "@/pages/email/CampaignWorkspace";

const PERIODS: { value: PeriodPreset; label: string }[] = [
  { value: "all", label: "All time" },
  { value: "7d", label: "Launched in the last 7 days" },
  { value: "30d", label: "Launched in the last 30 days" },
  { value: "custom", label: "Launched between…" },
];

type Tone = "slate" | "indigo" | "sky" | "violet" | "emerald";

const TONE: Record<Tone, { icon: string; bar: string }> = {
  slate: { icon: "bg-slate-100 text-slate-600", bar: "bg-slate-400" },
  indigo: { icon: "bg-indigo-50 text-indigo-600", bar: "bg-indigo-500" },
  sky: { icon: "bg-sky-50 text-sky-600", bar: "bg-sky-500" },
  violet: { icon: "bg-violet-50 text-violet-600", bar: "bg-violet-500" },
  emerald: { icon: "bg-emerald-50 text-emerald-600", bar: "bg-emerald-500" },
};

/** The activity chart's series: bars for who was launched / emailed, lines
 *  for what they did. Launched and Bounced start hidden. */
const SERIES: { key: ActivityKey; label: string; color: string; kind: "bar" | "line"; shown: boolean }[] = [
  { key: "launched", label: "Launched", color: "#e2e8f0", kind: "bar", shown: false },
  { key: "sent", label: "Emailed", color: "#a5b4fc", kind: "bar", shown: true },
  { key: "opened", label: "Opened", color: "#0ea5e9", kind: "line", shown: true },
  { key: "clicked", label: "Clicked", color: "#8b5cf6", kind: "line", shown: true },
  { key: "replied", label: "Replied", color: "#10b981", kind: "line", shown: true },
  { key: "bounced", label: "Bounced", color: "#f43f5e", kind: "line", shown: false },
];

/** A share as a bar width, 0–100%. */
function width(share: number | null | undefined): string {
  return `${Math.min(Math.max(share ?? 0, 0), 1) * 100}%`;
}

/**
 * Performance, from Prism's own records — never Lemlist's figures. Leads
 * (one person in this campaign) and messages are never mixed; every rate is
 * over the leads reached; a period is the cohort of leads launched in it.
 * All computed by the backend; this page only draws it.
 */
export default function PerformanceTab() {
  const { slug, campaign, version } = useWorkspace();
  const [params, setParams] = useSearchParams();
  const preset = (PERIODS.find((p) => p.value === params.get("period"))?.value ?? "all") as PeriodPreset;
  const customFrom = params.get("from") ?? "";
  const customTo = params.get("to") ?? "";
  const { launched_from: from, launched_to: to } = periodRange(preset, new Date(), {
    from: customFrom,
    to: customTo,
  });
  const [data, setData] = useState<CampaignPerformance | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  function update(next: Record<string, string | null>) {
    const merged = new URLSearchParams(params);
    for (const [key, value] of Object.entries(next)) {
      if (value === null || value === "") merged.delete(key);
      else merged.set(key, value);
    }
    setParams(merged, { replace: true });
  }

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getCampaignPerformance(slug, campaign.id, { launched_from: from, launched_to: to })
      .then((next) => !cancelled && setData(next))
      .catch((err) => !cancelled && setError(errorMessage(err, "Performance could not be loaded.")))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [slug, campaign.id, from, to, version, reload]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Select value={preset} onValueChange={(value) => update({ period: value === "all" ? null : value })}>
          <SelectTrigger className="h-9 w-[230px] rounded-lg border-slate-200 bg-white text-xs" aria-label="Period">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PERIODS.map((p) => (
              <SelectItem key={p.value} value={p.value}>
                {p.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {preset === "custom" && (
          <div className="flex items-center gap-1.5 text-xs text-slate-500">
            <Input
              type="date"
              value={customFrom}
              onChange={(e) => update({ from: e.target.value || null })}
              className="h-9 w-[140px] rounded-lg border-slate-200 bg-white text-xs"
              aria-label="Launched from"
            />
            <span>to</span>
            <Input
              type="date"
              value={customTo}
              onChange={(e) => update({ to: e.target.value || null })}
              className="h-9 w-[140px] rounded-lg border-slate-200 bg-white text-xs"
              aria-label="Launched to"
            />
          </div>
        )}
        <Button
          variant="ghost"
          size="sm"
          className="h-9 text-xs text-slate-500"
          onClick={() => setReload((n) => n + 1)}
          aria-label="Refresh"
        >
          <RefreshCcw className={cn("h-3.5 w-3.5", loading && "animate-spin")} />
        </Button>
        <p className="ml-auto flex items-center gap-1.5 text-xs text-slate-400">
          <Info className="h-3.5 w-3.5" />
          {preset === "all"
            ? "Every lead of the campaign"
            : `The leads launched in the period (${data?.timezone ?? "campaign timezone"})`}{" "}
          · from Prism&apos;s records
        </p>
      </div>

      {error ? (
        <div className="flex items-center gap-2 rounded-plane bg-white p-6 text-sm text-rose-600 shadow-plane">
          <AlertTriangle className="h-4 w-4" /> {error}
        </div>
      ) : !data ? (
        <div className="space-y-4" aria-busy="true">
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-3 xl:grid-cols-5">
            {[0, 1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-32 rounded-plane" />
            ))}
          </div>
          <Skeleton className="h-80 rounded-plane" />
          <div className="grid gap-4 lg:grid-cols-5">
            <Skeleton className="h-64 rounded-plane lg:col-span-3" />
            <Skeleton className="h-64 rounded-plane lg:col-span-2" />
          </div>
        </div>
      ) : (
        <Dashboard data={data} from={from} />
      )}
    </div>
  );
}

function Dashboard({ data, from }: { data: CampaignPerformance; from: string | null }) {
  const { funnel, rates } = data;
  const inCampaign = funnel.launched + data.leads_to_launch;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-3 xl:grid-cols-5">
        <Kpi
          icon={Rocket}
          tone="slate"
          label="Launched"
          hint={METRIC_HINT.launched}
          value={formatNumber(funnel.launched)}
          caption={data.leads_to_launch ? `${formatNumber(data.leads_to_launch)} still to launch` : "Nothing left to launch"}
          share={inCampaign ? funnel.launched / inCampaign : null}
        />
        <Kpi
          icon={Send}
          tone="indigo"
          label="Reached"
          hint={METRIC_HINT.reached}
          value={formatNumber(funnel.reached)}
          caption={funnel.launched ? `${rateLabel(funnel.reached / funnel.launched)} of the launched leads` : "No lead launched yet"}
          share={funnel.launched ? funnel.reached / funnel.launched : null}
        />
        <Kpi
          icon={MailOpen}
          tone="sky"
          label="Open rate"
          hint={METRIC_HINT.opened}
          value={rateLabel(rates.open_rate)}
          caption={`${plural(funnel.opened, "lead")} opened`}
          share={rates.open_rate}
        />
        <Kpi
          icon={MousePointerClick}
          tone="violet"
          label="Click rate"
          hint={METRIC_HINT.clicked}
          value={rateLabel(rates.click_rate)}
          caption={`${plural(funnel.clicked, "lead")} clicked`}
          share={rates.click_rate}
        />
        <Kpi
          icon={Reply}
          tone="emerald"
          label="Reply rate"
          hint={METRIC_HINT.replied}
          value={rateLabel(rates.reply_rate)}
          caption={`${plural(funnel.replied, "lead")} replied`}
          share={rates.reply_rate}
        />
      </div>

      <ActivityCard data={data} from={from} />

      <div className="grid gap-4 lg:grid-cols-5">
        <FunnelCard className="lg:col-span-3" funnel={funnel} rates={rates} />
        <StatesCard className="lg:col-span-2" states={data.states} />
      </div>

      <div className="grid gap-4 lg:grid-cols-5">
        <StepsCard className="lg:col-span-3" steps={data.steps} />
        <MessagesCard className="lg:col-span-2" messages={data.messages} dryRun={funnel.launched_dry_run} />
      </div>

      <p className="flex items-center gap-1.5 px-1 text-xs text-slate-400">
        <Info className="h-3.5 w-3.5 shrink-0" />
        No “delivered” figure: Lemlist reports no delivery, so Prism does not claim one. A rate shows — when no lead
        was reached.
      </p>
    </div>
  );
}

function Card({
  title,
  subtitle,
  icon: Icon,
  className,
  action,
  children,
}: {
  title: string;
  subtitle?: string;
  icon?: ComponentType<{ className?: string }>;
  className?: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className={cn("min-w-0 rounded-plane bg-white p-5 shadow-plane", className)}>
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-900">
            {Icon && <Icon className="h-4 w-4 text-indigo-500" />}
            {title}
          </h3>
          {subtitle && <p className="mt-0.5 text-xs text-slate-500">{subtitle}</p>}
        </div>
        {action}
      </header>
      {children}
    </section>
  );
}

function Kpi({
  icon: Icon,
  tone,
  label,
  hint,
  value,
  caption,
  share,
}: {
  icon: ComponentType<{ className?: string }>;
  tone: Tone;
  label: string;
  hint: string;
  value: string;
  caption: string;
  share: number | null;
}) {
  return (
    <section className="min-w-0 rounded-plane bg-white p-4 shadow-plane">
      <div className="flex items-center justify-between gap-2">
        <Hint content={hint}>
          <p className="truncate text-xs font-medium text-slate-500">{label}</p>
        </Hint>
        <span className={cn("flex h-8 w-8 shrink-0 items-center justify-center rounded-lg", TONE[tone].icon)}>
          <Icon className="h-4 w-4" />
        </span>
      </div>
      <p className="mt-2 text-[28px] font-semibold leading-none tracking-tight text-slate-900 tabular-nums">{value}</p>
      <p className="mt-1.5 truncate text-xs text-slate-500">{caption}</p>
      <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-slate-100">
        <div className={cn("h-full rounded-full transition-[width] duration-500", TONE[tone].bar)} style={{ width: width(share) }} />
      </div>
    </section>
  );
}

function ActivityCard({ data, from }: { data: CampaignPerformance; from: string | null }) {
  const timeline = useMemo(() => data.timeline ?? [], [data.timeline]);
  const { points, weekly } = useMemo(
    () => activitySeries(timeline, { today: isoDayIn(data.timezone), from }),
    [timeline, data.timezone, from],
  );
  const [shown, setShown] = useState<ReadonlySet<ActivityKey>>(
    () => new Set(SERIES.filter((s) => s.shown).map((s) => s.key)),
  );
  const totals = useMemo(() => {
    const out = {} as Record<ActivityKey, number>;
    for (const s of SERIES) out[s.key] = points.reduce((n, p) => n + p[s.key], 0);
    return out;
  }, [points]);
  const toggle = (key: ActivityKey) =>
    setShown((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  return (
    <Card
      title="Activity"
      subtitle={`Leads per ${weekly ? "week" : "day"} · days in ${data.timezone}`}
      icon={Activity}
      action={
        <div className="flex flex-wrap gap-1.5" role="group" aria-label="Series shown">
          {SERIES.map((s) => {
            const on = shown.has(s.key);
            return (
              <button
                key={s.key}
                type="button"
                aria-pressed={on}
                onClick={() => toggle(s.key)}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors",
                  on
                    ? "border-slate-200 bg-white text-slate-700 shadow-sm-tile"
                    : "border-transparent bg-slate-50 text-slate-400 hover:text-slate-600",
                )}
              >
                <span className="h-2 w-2 rounded-full" style={{ backgroundColor: on ? s.color : "#cbd5e1" }} />
                {s.label}
                <span className="tabular-nums text-slate-400">{formatNumber(totals[s.key])}</span>
              </button>
            );
          })}
        </div>
      }
    >
      <div className="relative mt-4 h-64">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={points} margin={{ top: 6, right: 6, left: -12, bottom: 0 }} barGap={2}>
            <CartesianGrid stroke="#eef2f7" vertical={false} />
            <XAxis
              dataKey="day"
              tickFormatter={(day: string) => format(parseISO(day), "d MMM")}
              tick={{ fontSize: 11, fill: "#94a3b8" }}
              axisLine={false}
              tickLine={false}
              minTickGap={18}
            />
            <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} width={36} />
            <Tooltip cursor={{ fill: "rgba(99, 102, 241, 0.06)" }} content={<ActivityTooltip weekly={weekly} />} />
            {SERIES.filter((s) => shown.has(s.key)).map((s) =>
              s.kind === "bar" ? (
                <Bar key={s.key} dataKey={s.key} name={s.label} fill={s.color} radius={[4, 4, 0, 0]} maxBarSize={22} isAnimationActive={false} />
              ) : (
                <Line
                  key={s.key}
                  dataKey={s.key}
                  name={s.label}
                  type="linear"
                  stroke={s.color}
                  strokeWidth={2}
                  dot={{ r: 2.5, strokeWidth: 0, fill: s.color }}
                  activeDot={{ r: 4 }}
                  isAnimationActive={false}
                />
              ),
            )}
          </ComposedChart>
        </ResponsiveContainer>
        {!timeline.length && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <p className="rounded-full bg-white/95 px-3 py-1.5 text-xs font-medium text-slate-500 shadow-sm-tile">
              No activity yet — the chart fills as emails go out
            </p>
          </div>
        )}
      </div>
    </Card>
  );
}

function ActivityTooltip({
  active,
  payload,
  label,
  weekly,
}: {
  active?: boolean;
  payload?: { dataKey?: string | number; name?: string; value?: number; color?: string }[];
  label?: string;
  weekly: boolean;
}) {
  if (!active || !payload?.length || !label) return null;
  const day = parseISO(label);
  return (
    <div className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs shadow-plane">
      <p className="mb-1.5 font-semibold text-slate-900">
        {weekly ? `Week of ${format(day, "d MMM")}` : format(day, "EEE d MMM yyyy")}
      </p>
      <div className="space-y-0.5">
        {payload.map((item) => (
          <p key={String(item.dataKey)} className="flex items-center justify-between gap-5">
            <span className="inline-flex items-center gap-1.5 text-slate-600">
              <span className="h-2 w-2 rounded-full" style={{ backgroundColor: item.color }} />
              {item.name}
            </span>
            <span className="font-semibold tabular-nums text-slate-900">{formatNumber(item.value ?? 0)}</span>
          </p>
        ))}
      </div>
    </div>
  );
}

function FunnelCard({ funnel, rates, className }: { funnel: LeadFunnel; rates: PerformanceRates; className?: string }) {
  const base = Math.max(funnel.launched, funnel.reached, 1);
  const stages = [
    { key: "launched", label: "Launched", value: funnel.launched, color: "bg-slate-400", note: "", hint: METRIC_HINT.launched },
    {
      key: "reached",
      label: "Reached",
      value: funnel.reached,
      color: "bg-indigo-500",
      note: funnel.launched ? `${rateLabel(funnel.reached / funnel.launched)} of launched` : "",
      hint: METRIC_HINT.reached,
    },
    {
      key: "opened",
      label: "Opened",
      value: funnel.opened,
      color: "bg-sky-500",
      note: `${rateLabel(rates.open_rate)} of reached`,
      hint: METRIC_HINT.opened,
    },
    {
      key: "replied",
      label: "Replied",
      value: funnel.replied,
      color: "bg-emerald-500",
      note: `${rateLabel(rates.reply_rate)} of reached`,
      hint: METRIC_HINT.replied,
    },
  ];
  return (
    <Card title="Lead funnel" subtitle="Unique leads · each rate over the leads reached" className={className}>
      <ol className="mt-4 space-y-2.5">
        {stages.map((stage, index) => (
          <li key={stage.key} className="grid grid-cols-[92px_minmax(0,1fr)_118px] items-center gap-3">
            <Hint content={stage.hint}>
              <span className="truncate text-xs font-medium text-slate-600">
                <span className="mr-1.5 tabular-nums text-slate-300">{index + 1}</span>
                {stage.label}
              </span>
            </Hint>
            <div className="relative h-8 overflow-hidden rounded-lg bg-slate-50">
              <div
                className={cn("h-full rounded-lg transition-[width] duration-500", stage.color)}
                style={{ width: stage.value ? `max(${width(stage.value / base)}, 2.25rem)` : "0%" }}
              />
              <span
                className={cn(
                  "absolute inset-y-0 left-2.5 flex items-center text-xs font-semibold tabular-nums",
                  stage.value ? "text-white" : "text-slate-400",
                )}
              >
                {formatNumber(stage.value)}
              </span>
            </div>
            <span className="text-right text-xs tabular-nums text-slate-500">{stage.note}</span>
          </li>
        ))}
      </ol>
      <div className="mt-4 grid grid-cols-3 gap-2 border-t border-slate-100 pt-4">
        <MiniStat label="Clicked" hint={METRIC_HINT.clicked} value={funnel.clicked} rate={rates.click_rate} dot="bg-violet-500" />
        <MiniStat label="Bounced" hint={METRIC_HINT.bounced} value={funnel.bounced} rate={rates.bounce_rate} dot="bg-rose-500" />
        <MiniStat
          label="Unsubscribed"
          hint={METRIC_HINT.unsubscribed}
          value={funnel.unsubscribed}
          rate={rates.unsubscribe_rate}
          dot="bg-zinc-500"
        />
      </div>
    </Card>
  );
}

function MiniStat({
  label,
  hint,
  value,
  rate,
  dot,
}: {
  label: string;
  hint: string;
  value: number;
  rate: number | null;
  dot: string;
}) {
  return (
    <Hint content={hint}>
      <div className="min-w-0 rounded-xl bg-slate-50 px-3 py-2">
        <p className="flex items-center gap-1.5 truncate text-xs text-slate-500">
          <span className={cn("h-1.5 w-1.5 shrink-0 rounded-full", dot)} />
          {label}
        </p>
        <p className="mt-0.5 flex items-baseline gap-1.5">
          <span className="text-base font-semibold tabular-nums text-slate-900">{formatNumber(value)}</span>
          <span className="text-xs tabular-nums text-slate-400">{rateLabel(rate)}</span>
        </p>
      </div>
    </Hint>
  );
}

function StatesCard({ states, className }: { states: Record<string, number>; className?: string }) {
  const entries = CONTACT_STATUS_ORDER.filter((s) => states[s]).map((s) => [s, states[s]] as const);
  const total = entries.reduce((n, [, count]) => n + count, 0);
  return (
    <Card title="Where the leads are" subtitle="Leads by state now" className={className}>
      {total ? (
        <>
          <div className="mt-4 flex h-3 gap-0.5 overflow-hidden rounded-full bg-slate-100">
            {entries.map(([state, count]) => (
              <span
                key={state}
                title={`${CONTACT_STATUS[state].label}: ${formatNumber(count)}`}
                className={cn("h-full first:rounded-l-full last:rounded-r-full", CONTACT_STATUS[state].dot)}
                style={{ width: `${(count / total) * 100}%` }}
              />
            ))}
          </div>
          <ul className="mt-4 space-y-1.5">
            {entries.map(([state, count]) => (
              <li key={state} className="flex items-center justify-between gap-3 text-sm">
                <Hint content={CONTACT_STATUS[state].hint}>
                  <span className="inline-flex min-w-0 items-center gap-2 text-slate-600">
                    <span className={cn("h-2 w-2 shrink-0 rounded-full", CONTACT_STATUS[state].dot)} />
                    <span className="truncate">{CONTACT_STATUS[state].label}</span>
                  </span>
                </Hint>
                <span className="shrink-0 tabular-nums">
                  <span className="font-semibold text-slate-900">{formatNumber(count)}</span>
                  <span className="ml-2 inline-block w-9 text-right text-xs text-slate-400">
                    {Math.round((count / total) * 100)}%
                  </span>
                </span>
              </li>
            ))}
          </ul>
        </>
      ) : (
        <p className="mt-6 text-center text-sm text-slate-400">No lead in this period.</p>
      )}
    </Card>
  );
}

function StepsCard({ steps, className }: { steps: StepPerformance[]; className?: string }) {
  return (
    <Card
      title="By step"
      subtitle="Each rate over the leads the step's email was sent to · a reply counts for the step it answers"
      icon={ListOrdered}
      className={className}
    >
      {steps.length ? (
        <ol className="mt-2 divide-y divide-slate-100">
          {steps.map((step) => (
            <li key={step.position ?? "none"} className="py-3 last:pb-0">
              <div className="flex items-center justify-between gap-3">
                {step.position === null ? (
                  <span className="text-xs text-slate-500">No step — tied to none by Lemlist</span>
                ) : (
                  <span className="flex min-w-0 items-center gap-2">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-indigo-50 text-xs font-semibold text-indigo-600">
                      {step.position}
                    </span>
                    <span className="truncate text-sm font-medium text-slate-800">{step.name ?? "Deleted step"}</span>
                  </span>
                )}
                <Hint content={METRIC_HINT.entered}>
                  <span className="shrink-0 text-xs text-slate-500">
                    sent to <span className="font-semibold tabular-nums text-slate-900">{plural(step.entered, "lead")}</span>
                  </span>
                </Hint>
              </div>
              <div className="mt-2.5 grid grid-cols-3 gap-3">
                <RateBar label="Opened" value={step.opened} rate={step.open_rate} color="bg-sky-500" />
                <RateBar label="Clicked" value={step.clicked} rate={step.click_rate} color="bg-violet-500" />
                <RateBar label="Replied" value={step.replied} rate={step.reply_rate} color="bg-emerald-500" />
              </div>
              {(step.bounced > 0 || step.unsubscribed > 0) && (
                <p className="mt-2 text-xs text-slate-500">
                  {step.bounced > 0 && <span className="text-rose-600">{plural(step.bounced, "bounce")}</span>}
                  {step.bounced > 0 && step.unsubscribed > 0 && " · "}
                  {step.unsubscribed > 0 && <span>{plural(step.unsubscribed, "unsubscribe")}</span>}
                </p>
              )}
            </li>
          ))}
        </ol>
      ) : (
        <p className="mt-6 text-center text-sm text-slate-400">No step yet.</p>
      )}
    </Card>
  );
}

function RateBar({ label, value, rate, color }: { label: string; value: number; rate: number | null; color: string }) {
  return (
    <div className="min-w-0">
      <div className="flex items-baseline justify-between gap-2 text-xs">
        <span className="truncate text-slate-500">{label}</span>
        <span className="shrink-0 tabular-nums">
          <span className="font-semibold text-slate-900">{rateLabel(rate)}</span>
          <span className="ml-1 text-slate-400">{formatNumber(value)}</span>
        </span>
      </div>
      <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-slate-100">
        <div className={cn("h-full rounded-full transition-[width] duration-500", color)} style={{ width: width(rate) }} />
      </div>
    </div>
  );
}

function MessagesCard({ messages, dryRun, className }: { messages: MessageTotals; dryRun: number; className?: string }) {
  const items: { label: string; note?: string; value: number; hint: string }[] = [
    { label: "Sent", value: messages.sent, hint: METRIC_HINT.sent },
    { label: "Opened", value: messages.opened, hint: METRIC_HINT.opened_messages },
    { label: "Opens", note: "repeats included", value: messages.opens, hint: METRIC_HINT.opens },
    { label: "Clicks", note: "repeats included", value: messages.clicks, hint: METRIC_HINT.clicks },
    { label: "Replies", value: messages.replies, hint: METRIC_HINT.replies },
    { label: "Bounced", value: messages.bounced, hint: METRIC_HINT.bounced_messages },
  ];
  return (
    <Card title="Emails" subtitle="Messages, not leads" icon={Send} className={className}>
      <div className="mt-3 grid grid-cols-2 gap-2">
        {items.map((item) => (
          <Hint key={item.label} content={item.hint}>
            <div className="min-w-0 rounded-xl bg-slate-50 px-3 py-2.5">
              <p className="truncate text-xs text-slate-500">
                {item.label}
                {item.note && <span className="text-slate-400"> · {item.note}</span>}
              </p>
              <p className="mt-0.5 text-lg font-semibold tabular-nums text-slate-900">{formatNumber(item.value)}</p>
            </div>
          </Hint>
        ))}
      </div>
      {dryRun > 0 && (
        <p className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
          {plural(dryRun, "lead")} launched in a dry run — simulated, nothing was sent to them.
        </p>
      )}
    </Card>
  );
}
